import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

export function initSentry() {
  // Only initialize in production or staging
  if (import.meta.env.MODE === 'development') {
    console.info('Sentry disabled in development mode');
    return;
  }

  Sentry.init({
    dsn: 'https://1defff6626e47be25fc370444ca4ad9e@o4509520906354688.ingest.us.sentry.io/4509520907337729',

    environment: import.meta.env.MODE,

    integrations: [
      new BrowserTracing({
        // Set sampling to 10% for performance monitoring
        tracingOrigins: ['localhost', 'sphericalmanager.pages.dev', /^\//],
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          window.history
        ),
      }),
      new Sentry.Replay({
        // Mask all text and inputs for privacy
        maskAllText: true,
        maskAllInputs: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0.3,

    // Replay sampling (moved from integration to main config)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Security token for outbound requests
    tunnel: undefined, // Use default Sentry endpoint

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'chrome-extension://',
      'moz-extension://',
      // Common non-errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed',
      'Non-Error promise rejection captured',
      // Network errors
      'NetworkError',
      'Network request failed',
    ],

    beforeSend(event, hint) {
      // Filter out non-error events in production
      if (import.meta.env.MODE === 'production') {
        // Don't send events without an exception
        if (!hint.originalException) {
          return null;
        }

        // Filter out 401/403 auth errors (expected behavior)
        const error = hint.originalException as any;
        if (error?.status === 401 || error?.status === 403) {
          return null;
        }
      }

      // Sanitize sensitive data
      if (event.request?.cookies) {
        delete event.request.cookies;
      }

      return event;
    },
  });
}

// Export Sentry for use in error boundaries and manual error capture
export { Sentry };
